import {
  BufferAttribute,
  type BufferGeometry,
  Float16BufferAttribute,
  Float32BufferAttribute,
  Int8BufferAttribute,
  Int16BufferAttribute,
  Int32BufferAttribute,
  type TypedArrayConstructor,
  Uint8BufferAttribute,
  Uint8ClampedBufferAttribute,
  Uint16BufferAttribute,
  Uint32BufferAttribute,
} from "three";

// This is a best-effort finalizer for bounded, finished meshes, not a general
// optimizer for multi-million-corner foam or arbitrary imported assets.
const MAX_ELEMENTS = 1_000_000;
const MAX_INPUT_BYTES = 32 * 1024 * 1024;
const MAX_HASH_CANDIDATES = 64;

const arrayConstructors = new Set<unknown>([
  Int8Array,
  Uint8Array,
  Uint8ClampedArray,
  Int16Array,
  Uint16Array,
  Int32Array,
  Uint32Array,
  Float32Array,
  Float64Array,
]);

// Only these constructors have known allocation/interpretation semantics.
// In particular, half floats must remain Float16BufferAttributes, not merely
// Uint16-backed BufferAttributes. Unknown subclasses are left untouched.
const attributeConstructors = new Map<unknown, TypedArrayConstructor>([
  [Int8BufferAttribute, Int8Array],
  [Uint8BufferAttribute, Uint8Array],
  [Uint8ClampedBufferAttribute, Uint8ClampedArray],
  [Int16BufferAttribute, Int16Array],
  [Uint16BufferAttribute, Uint16Array],
  [Int32BufferAttribute, Int32Array],
  [Uint32BufferAttribute, Uint32Array],
  [Float16BufferAttribute, Uint16Array],
  [Float32BufferAttribute, Float32Array],
]);

interface AttributeSource {
  name: string;
  attribute: BufferAttribute;
  view: DataView;
  stride: number;
}

function isSupportedAttribute(value: unknown): value is BufferAttribute {
  if (!(value instanceof BufferAttribute)) return false;
  if (
    "isInstancedBufferAttribute" in value &&
    value.isInstancedBufferAttribute
  ) {
    return false;
  }

  const { array, itemSize, count } = value;
  return (
    ArrayBuffer.isView(array) &&
    arrayConstructors.has(array.constructor) &&
    // Shared storage could change between hashing, comparison and copying.
    array.buffer instanceof ArrayBuffer &&
    (value.constructor === BufferAttribute ||
      attributeConstructors.get(value.constructor) === array.constructor) &&
    Number.isSafeInteger(itemSize) &&
    itemSize > 0 &&
    Number.isSafeInteger(count) &&
    count === array.length / itemSize
  );
}

function isSupportedIndex(index: BufferAttribute): boolean {
  return (
    isSupportedAttribute(index) &&
    index.itemSize === 1 &&
    !index.normalized &&
    !(index instanceof Float16BufferAttribute) &&
    (index.array instanceof Uint8Array ||
      index.array instanceof Uint16Array ||
      index.array instanceof Uint32Array)
  );
}

function hasInvalidIndices(
  index: BufferAttribute | null,
  vertexCount: number,
): boolean {
  if (!index) return false;
  const restartIndex = 2 ** (index.array.BYTES_PER_ELEMENT * 8) - 1;
  for (const vertex of index.array) {
    if (vertex >= vertexCount || vertex === restartIndex) return true;
  }
  return false;
}

function hashVertex(
  sources: readonly AttributeSource[],
  vertex: number,
): number {
  let hash = 0x811c9dc5;
  for (const { view, stride } of sources) {
    let offset = vertex * stride;
    const end = offset + stride;
    // Hash raw bytes in word-sized chunks where possible. Arithmetic wraps
    // only the hash, never attribute values; collisions are checked below.
    for (; offset + 4 <= end; offset += 4) {
      hash = Math.imul(hash ^ view.getUint32(offset, true), 0x01000193);
      // Spread high float/sign bits even for values with many zero low bits.
      hash ^= hash >>> 16;
    }
    for (; offset < end; offset++) {
      hash = Math.imul(hash ^ view.getUint8(offset), 0x01000193);
      hash ^= hash >>> 16;
    }
  }
  return hash;
}

function equalVertex(
  sources: readonly AttributeSource[],
  left: number,
  right: number,
): boolean {
  for (const { view, stride } of sources) {
    const a = left * stride;
    const b = right * stride;
    let offset = 0;
    for (; offset + 4 <= stride; offset += 4) {
      if (
        view.getUint32(a + offset, true) !== view.getUint32(b + offset, true)
      ) {
        return false;
      }
    }
    for (; offset < stride; offset++) {
      if (view.getUint8(a + offset) !== view.getUint8(b + offset)) {
        return false;
      }
    }
  }
  return true;
}

/** Allocate the same supported attribute class without cloning its old data. */
function allocateAttribute(
  source: BufferAttribute,
  count: number,
): BufferAttribute {
  const buffer = new ArrayBuffer(
    count * source.itemSize * source.array.BYTES_PER_ELEMENT,
  );
  const ArrayType = source.array.constructor as TypedArrayConstructor;
  // Three's typed attribute constructors accept an ArrayBuffer and create a
  // view over it; the base BufferAttribute instead needs the typed view.
  const AttributeType = source.constructor as new (
    array: ArrayBuffer,
    itemSize: number,
    normalized: boolean,
  ) => BufferAttribute;
  const attribute =
    source.constructor === BufferAttribute
      ? new BufferAttribute(
          new ArrayType(buffer),
          source.itemSize,
          source.normalized,
        )
      : new AttributeType(buffer, source.itemSize, source.normalized);
  attribute.name = source.name;
  attribute.gpuType = source.gpuType;
  attribute.setUsage(source.usage);
  attribute.onUpload(source.onUploadCallback);
  // Fresh buffers upload in full. Old update ranges refer to old vertex IDs.
  return attribute;
}

function copyAttributes(
  sources: readonly AttributeSource[],
  representatives: readonly number[],
) {
  const uniqueCount = representatives.length;
  return sources.map(({ name, attribute, view, stride }) => {
    const compacted = allocateAttribute(attribute, uniqueCount);
    const target = new Uint8Array(
      compacted.array.buffer,
      compacted.array.byteOffset,
      compacted.array.byteLength,
    );
    const source = new Uint8Array(
      view.buffer,
      view.byteOffset,
      view.byteLength,
    );
    let destination = 0;
    for (const vertex of representatives) {
      const offset = vertex * stride;
      target.set(source.subarray(offset, offset + stride), destination);
      destination += stride;
    }
    return { name, attribute: compacted };
  });
}

function allocateIndex(
  index: BufferAttribute | null,
  cornerCount: number,
  indexBytes: number,
): BufferAttribute {
  if (index) return allocateAttribute(index, cornerCount);
  return new BufferAttribute(
    indexBytes === 2
      ? new Uint16Array(cornerCount)
      : new Uint32Array(cornerCount),
    1,
  );
}

function collectSources(
  geometry: BufferGeometry,
  vertexCount: number,
  index: BufferAttribute | null,
) {
  let originalBytes = index?.array.byteLength ?? 0;
  let bytesPerVertex = 0;
  const sources: AttributeSource[] = [];
  const buffers = new Set<ArrayBufferLike>();
  if (index) buffers.add(index.array.buffer);
  for (const [name, attribute] of Object.entries(geometry.attributes)) {
    if (!isSupportedAttribute(attribute) || attribute.count !== vertexCount) {
      return;
    }
    const { array, itemSize } = attribute;
    // Do not double-count storage shared by attributes or change its aliasing.
    if (buffers.has(array.buffer)) return;
    buffers.add(array.buffer);
    originalBytes += array.byteLength;
    if (originalBytes > MAX_INPUT_BYTES) return;
    const stride = itemSize * array.BYTES_PER_ELEMENT;
    bytesPerVertex += stride;
    sources.push({
      name,
      attribute,
      stride,
      view: new DataView(array.buffer, array.byteOffset, array.byteLength),
    });
  }

  if (hasInvalidIndices(index, vertexCount)) return;
  return { sources, originalBytes, bytesPerVertex };
}

function commitCompaction<T extends BufferGeometry>(
  geometry: T,
  sources: readonly AttributeSource[],
  index: BufferAttribute | null,
  representatives: readonly number[],
  remap: Uint32Array,
  originalBytes: number,
  bytesPerVertex: number,
): T {
  const cornerCount = index?.count ?? remap.length;
  const uniqueCount = representatives.length;
  // Match Three's setIndex boundary: 65535 is a Uint16 primitive restart, not
  // a usable vertex index. Existing valid index types never need widening.
  const indexBytes =
    index?.array.BYTES_PER_ELEMENT ?? (uniqueCount > 65_535 ? 4 : 2);
  if (
    uniqueCount * bytesPerVertex + cornerCount * indexBytes >=
    originalBytes
  ) {
    return geometry;
  }

  // Allocate/copy everything before committing; never mutate source buffers.
  const attributes = copyAttributes(sources, representatives);
  const compactedIndex = allocateIndex(index, cornerCount, indexBytes);
  for (let corner = 0; corner < cornerCount; corner++) {
    const mapped = remap[index?.array[corner] ?? corner];
    if (!mapped) return geometry;
    compactedIndex.array[corner] = mapped - 1;
  }

  for (const { name, attribute } of attributes) {
    geometry.setAttribute(name, attribute);
  }
  geometry.setIndex(compactedIndex);
  return geometry;
}

function compactGeometryData<T extends BufferGeometry>(
  geometry: T,
  vertexCount: number,
  index: BufferAttribute | null,
  cornerCount: number,
): T {
  const input = collectSources(geometry, vertexCount, index);
  if (!input) return geometry;
  const { sources, originalBytes, bytesPerVertex } = input;

  // Keep the hot loop with its fresh arrays: passing them into a remap helper
  // prevents V8 from optimizing their accesses as effectively.
  // Linked candidates avoid allocating an array for every distinct hash.
  const heads = new Map<number, number>();
  const representatives: number[] = [];
  const nextCandidates: number[] = [];
  const remap = new Uint32Array(vertexCount); // destination + 1; 0 = unseen
  for (let corner = 0; corner < cornerCount; corner++) {
    // Validate/remap the FULL stream, not only active drawRange/groups.
    const vertex = index?.array[corner] ?? corner;
    if (remap[vertex]) continue;
    const hash = hashVertex(sources, vertex);
    const head = heads.get(hash) ?? 0;
    let candidate = head;
    let attempts = 0;
    while (candidate !== 0) {
      if (++attempts > MAX_HASH_CANDIDATES) return geometry;
      const other = representatives[candidate - 1];
      if (other === undefined) return geometry;
      if (equalVertex(sources, vertex, other)) break;
      candidate = nextCandidates[candidate - 1] ?? 0;
    }
    if (candidate === 0) {
      representatives.push(vertex);
      nextCandidates.push(head);
      candidate = representatives.length;
      heads.set(hash, candidate);
    }
    remap[vertex] = candidate;
  }

  return commitCompaction(
    geometry,
    sources,
    index,
    representatives,
    remap,
    originalBytes,
    bytesPerVertex,
  );
}

/**
 * Opt-in, in-place exact indexing of a finished triangle mesh.
 *
 * Call AFTER final normals, UVs and custom attributes, but BEFORE GPU upload,
 * cache/mesh publication or building topology-dependent data. Never use this
 * to update live/shared geometry: no dispose event is emitted for old buffers.
 * External vertex IDs (including gl_VertexID) are not preserved or remapped.
 *
 * Every attribute's raw bits form the key, including signed zero, NaN payloads
 * and normalized integers. The complete corner stream is retained, so winding,
 * triangle order, groups and drawRange stay unchanged. Indexed inputs may lose
 * unreferenced vertices; existing bounds and all geometry metadata stay as-is.
 * Existing index types/metadata are retained; new indices use restart-safe
 * Uint16/Uint32 storage. Nothing changes unless attribute + index bytes shrink.
 *
 * Unsupported layouts are a no-op: morphs, instancing, interleaved/GPU/shared
 * or aliased buffers, unknown attribute subclasses, invalid counts/indices
 * and incomplete triangles. Work is bounded to 1M source vertices/corners,
 * 32 MiB input buffers and 64 hash candidates per lookup. No mesh traversal.
 */
export function compactGeometry<T extends BufferGeometry>(geometry: T): T {
  if (
    Object.keys(geometry.morphAttributes).length > 0 ||
    geometry.indirect ||
    ("isInstancedBufferGeometry" in geometry &&
      geometry.isInstancedBufferGeometry)
  ) {
    return geometry;
  }

  const position = geometry.getAttribute("position");
  if (!isSupportedAttribute(position)) return geometry;
  const vertexCount = position.count;
  if (vertexCount === 0 || vertexCount > MAX_ELEMENTS) return geometry;

  const index = geometry.getIndex();
  if (index && !isSupportedIndex(index)) return geometry;
  const cornerCount = index?.count ?? vertexCount;
  if (
    cornerCount === 0 ||
    cornerCount > MAX_ELEMENTS ||
    cornerCount % 3 !== 0
  ) {
    return geometry;
  }

  return compactGeometryData(geometry, vertexCount, index, cornerCount);
}
